// @ts-nocheck

import { some } from 'lodash';
import { matchPath } from 'react-router-dom';
import { createSelector } from 'reselect';

import Client from '../../client';
import { getRedirectUrl, RedirectServices } from '../../config/urls';
import { getAppDOMElement } from '../../DOMElements';
import { getLang } from '../../i18n/l10n';
import { skipAllKLTemporaryNotifications } from '../../modules/koh-lanta/actions/kl';
import {
  getDispatchFromProps,
  getHistoryFromProps,
  getLocationFromProps,
  getOpenBoomViewFromProps,
  getOpenExternalLinkFromProps,
  getOpenGRViewFromProps,
  getToggleMenuFromProps,
} from '../../selectors/_propsGetters_';
import { selectRuntimeLicence } from '../../selectors/runtime/runtimePrimary.selector';
import {
  selectUserConnected,
  selectUserConnecting,
  selectUserLastSessionSettledBets,
} from '../../selectors/user/userPrimary.selector';
import { type Licence } from '../../types/common';
import { Locales } from '../../types/common';
import { RoutePatterns } from '../../types/routes';
import { type Selector } from '../../types/selectors';
import { navigateBack } from '../../utils/navigation';
import Routes from '../../utils/routes';
import isBetbuilder from '../../utils/routes/isBetbuilder';
import isLineUp from '../../utils/routes/isLineUp';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import { getAnchorIdByPathname } from '../../utils/scroll';
import { smartScrollToRootAnchor, SMOOTH_SCROLL_TIMEOUT } from '../../utils/smoothScroll';

const RoutesNeedingBackButton = [
  RoutePatterns.MATCH,
  RoutePatterns.MORE,
  RoutePatterns.HISTORY_ITEM_DETAIL,
  RoutePatterns.SPORTS,
  RoutePatterns.CATALOG,
  RoutePatterns.CALENDAR,
  RoutePatterns.KL_ROUND_RULES,
  RoutePatterns.KL_BADGE_DETAILS,
  RoutePatterns.KL_ORIENTATION_STEP_DETAIL,
  RoutePatterns.GR_ROUND_RULES,
  RoutePatterns.GRAPHICS_PREFERENCES,
  RoutePatterns.MP_FAMILY_DETAIL,
  RoutePatterns.BOOM_STEP,
];

type NavigationBarPropsType = {
  isConnected: boolean;
  isConnecting: boolean;
  showConnectButton: boolean;
  onMenuClick: () => void;
  onLogoClick: () => void;
  onGoBack: () => void;
  onClose: () => void;
  onConnectButtonClick: () => void;
  onUserClick: () => void;
  onPokerButtonClick: () => void;
  isWinModalOpen: boolean;
  licence: Licence;
};

const selectNavigationBarProps: Selector<NavigationBarPropsType> = createSelector(
  [
    getHistoryFromProps,
    getLocationFromProps,
    getToggleMenuFromProps,
    getOpenExternalLinkFromProps,
    selectUserConnected,
    selectUserConnecting,
    selectRuntimeLicence,
    selectUserLastSessionSettledBets,
    getDispatchFromProps,
    (state) => !!state.kl.misc?.tmpNotifications?.length,
    getOpenGRViewFromProps,
    getOpenBoomViewFromProps,
  ],
  (
    history,
    location,
    toggleMenu,
    openExternalLink,
    connected,
    connecting,
    licence,
    lastSessionSettledBets,
    dispatch,
    hasKLNotifications,
    openGRView,
    openBoomView,
  ) => {
    const isConnected = connected;
    const isConnecting = connecting;
    let onGoBack = null;
    let onClose = null;
    let onConnectButtonClick = null;
    let onPokerButtonClick = null;
    let showConnectButton = true;

    if (
      !!location.state?.playerWidgetProps ||
      ~location.hash.indexOf('#playerStats') ||
      ~location.hash.indexOf('#search') ||
      ~location.hash.indexOf('#multiplexEventList') ||
      ~location.hash.indexOf('#share=') ||
      ~location.hash.indexOf('#liveTable') ||
      ~location.hash.indexOf('#cupRoster') ||
      ~location.hash.indexOf('#cupRoster') ||
      ~location.hash.indexOf('#mission-details') ||
      isBetbuilder(location.pathname) ||
      isLineUp(location.pathname)
    ) {
      onClose = () => navigateBack({ history, location, openGRView, openBoomView });
    } else if (Routes.isKL(location.pathname, { exact: false }) && hasKLNotifications) {
      onClose = () => {
        dispatch(skipAllKLTemporaryNotifications());
      };
    } else if (some(RoutesNeedingBackButton, (p) => matchPath(location.pathname, { path: p, exact: true }) !== null)) {
      // On exclu la route /menu qui est la racine du menu sport
      if (location.pathname !== '/menu') {
        onGoBack = () => navigateBack({ history, location, dispatch, openGRView, openBoomView });

        // On masque le bouton back pour les vues catalogues si on ne vient pas du sportMenu
        // https://gitlab.dev.winamax.fr/betting-clients/winabet/-/issues/392NavigationBarContainer.jsx
        if (
          !Routes.isSports(history.getPreviousPath()) &&
          !Routes.isSports(history.getParentPath()) &&
          Routes.isCatalog(history.location.pathname)
        ) {
          onGoBack = null;
        }
      }
    }

    if (!isConnected) {
      if (~location.hash.indexOf('#login')) {
        showConnectButton = false;
      }
      onConnectButtonClick = () => history.push(RoutesRedirectionFactory.getLoginRouteConfig());
    }

    if (getLang() !== Locales.DE) {
      onPokerButtonClick = () => {
        if (Client.embed) {
          window.location.href = 'wam://signin?target=poker';
        } else {
          openExternalLink(getRedirectUrl(RedirectServices.PLAY_POKER), { auth: true });
        }
      };
    }

    return {
      isConnected,
      isConnecting,
      isWinModalOpen: lastSessionSettledBets.length > 0,
      licence: licence,
      showConnectButton,
      onMenuClick: toggleMenu,
      onLogoClick: () => {
        const scrollTop = getAppDOMElement()?.scrollTop ?? 0;
        const pathname = history?.location?.pathname;
        const anchorId = getAnchorIdByPathname(pathname);

        if (scrollTop && anchorId) {
          smartScrollToRootAnchor(anchorId, SMOOTH_SCROLL_TIMEOUT, 0);
        } else {
          history.push(RoutesRedirectionFactory.getHomeRouteConfig());
        }
      },
      onGoBack,
      onClose,
      onConnectButtonClick,
      onUserClick: () => history.push(RoutesRedirectionFactory.getUserAccountRouteConfig()),
      onPokerButtonClick,
    };
  },
);

export default selectNavigationBarProps;
